<template>
  <div>
    <div v-if="overlay" class="fixed top-0 left-0 h-full w-full z-30">
      <div
        class="absolute left-0 top-0 w-full h-full overlay-bg bg-black"
      ></div>
      <div class="bg-gray-base overlay-content">
        <StoryHero
          :active="true"
          :to="overlay.link"
          :hero="overlay.hero"
          :caption="overlay.title"
        />
      </div>
    </div>
    <StoryHero
      :key="hero._id"
      :active="hero._id === active"
      :to="hero.link"
      :hero="hero.hero"
      :caption="hero.title"
      ref="heroRef"
      @activate="(el) => onActivateHero(el, hero._id)"
      @animated="onAnimated(hero)"
    />
    <template v-if="supporting.length">
      <div class="absolute h-screen w-px" ref="listRef" />

      <div class="my-36">
        <StoryListingComponent
          v-for="(story, i) of supporting"
          :key="story._id"
          :active="story._id === active"
          :to="story.link"
          :hero="story.hero"
          :date="story.publishedDate"
          :title-top="story.titleTop"
          :title-bottom="story.titleBottom"
          :show-border="i !== supporting.length - 1"
          @activate="(el) => onActivateListing(story)"
          @animated="onAnimated(story)"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {
  ComponentPublicInstance,
  computed,
  defineComponent,
  onBeforeUpdate,
  onMounted,
  PropType,
  ref,
} from 'vue';
import animateScrollTo from 'animated-scroll-to';
import { useRouter } from 'vue-router';
import { useStore } from '@/store';
import StoryHero from '@/components/StoryHero.vue';
import StoryListingComponent from '@/components/StoryListing.vue';
import { StoryListing } from '@/types';

export default defineComponent({
  components: {
    StoryHero,
    StoryListingComponent,
  },
  props: {
    stories: {
      type: Array as PropType<StoryListing[]>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();
    const store = useStore();
    const active = ref('');
    const overlay = ref<StoryListing | false>(false);

    const hero = computed(() => props.stories[0]);
    const supporting = computed(() => props.stories.slice(1));

    const heroRef = ref<ComponentPublicInstance | null>(null);
    const listRef = ref<HTMLElement | null>(null);

    store.commit.setNavLight(props.stories[0]?.hero.lightText);

    const onActivateHero = (el: HTMLElement, key: string) => {
      animateScrollTo(el.offsetTop, {
        cancelOnUserAction: false,
        maxDuration: 1000,
      }).then((hasScrolledToPosition) => {
        if (hasScrolledToPosition) {
          document.body.classList.add('overflow-hidden');
          store.commit.setNavLight(false);
          active.value = key;
        }
      });
    };

    const onActivateListing = (story: StoryListing) => {
      document.body.classList.add('overflow-hidden');
      store.commit.setNavLight(false);
      active.value = story._id;
      overlay.value = story;
    };

    const onAnimated = (story: StoryListing) => {
      document.body.classList.remove('overflow-hidden');
      router.push(story.link);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.some((entry) => {
          if (entry.isIntersecting) {
            const isHero = entry.target === heroRef.value?.$el;
            if (entry.intersectionRatio > 0.5 && !isHero) {
              store.commit.setNavLight(false);
              return true;
            } else if (entry.intersectionRatio < 0.5 && isHero) {
              store.commit.setNavLight(hero.value.hero.lightText);
              return true;
            }
          }
        });
      },
      { threshold: [0.05, 0.95] }
    );

    onMounted(() => {
      if (heroRef.value && listRef.value) {
        observer.observe(heroRef.value.$el);
        observer.observe(listRef.value);
      }
    });
    onBeforeUpdate(() => {
      heroRef.value = null;
      listRef.value = null;
    });
    return {
      active,
      overlay,
      hero,
      heroRef,
      onAnimated,
      listRef,
      onActivateHero,
      onActivateListing,
      supporting,
    };
  },
});
</script>

<style lang="postcss" scoped>
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes slideIn {
  0% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(0);
  }
}
.overlay-bg {
  animation: fadeIn ease 720ms;
}
.overlay-content {
  animation: slideIn ease 720ms;
}
</style>
